import { forwardRef } from 'react';
import { Platform, StyleSheet, TextInput, type TextInputProps, type ViewStyle } from 'react-native';
import { colors } from '../../theme/colors';
import { fieldFontFamily, fieldPlaceholderColor } from '../../theme/typography';

/**
 * Branded text field. A thin wrapper over `TextInput` that bakes in the shared
 * field tokens — the Geist UI font, the single placeholder color, and the
 * web outline-kill — while passing through the call site's own style/border.
 *
 * Use this instead of a raw `<TextInput>` so every field across the app stays
 * on-brand and never shows the off-brand default browser focus outline.
 */
export const FormInput = forwardRef<TextInput, TextInputProps>(
  ({ style, placeholderTextColor, ...props }, ref) => (
    <TextInput
      ref={ref}
      placeholderTextColor={placeholderTextColor ?? fieldPlaceholderColor}
      style={[styles.base, style]}
      {...props}
    />
  ),
);

FormInput.displayName = 'FormInput';

const styles = StyleSheet.create({
  base: {
    fontFamily: fieldFontFamily,
    color: colors.text.primary,
    ...Platform.select({
      web: { outlineStyle: 'none', outlineWidth: 0 } as unknown as ViewStyle,
    }),
  },
});
